import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { Input } from '@components/Input';
import { ExternalLink } from '@components/Link';
import { Trans } from 'react-i18next';
import { alertActions, accountActions } from '@actions';
import { vtexIntegrationService } from '@services';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { handleApiError } from '@helpers/api';
import { ExternalRoutes } from '@constants/routes';
import { Content, Modal } from './styles';

const VTEXModal = ({ isOpen, onClose, onConnected }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const language = useSelector(state => state.auth.user?.language);

    const VTEXSchema = yup.object().shape({
        accountName: yup.string().required('validation.required'),
        applicationToken: yup.string().required('validation.required'),
        applicationKey: yup.string().required('validation.required'),
    });

    const resolver = useYupValidationResolver(VTEXSchema);

    const {
        getValues,
        control,
        formState: { isValid, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            accountName: '',
            applicationToken: '',
            applicationKey: '',
        },
    });

    const onConnect = async () => {
        try {
            const values = getValues();
            await vtexIntegrationService.connectIntegration(values);
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            onConnected?.();
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    return (
        <Modal
            fullScreen
            isOpen={isOpen}
            handleClose={onClose}
            title={t('SettingsIntegrations.vtexTitle')}
            subtitle={
                <Trans t={t} i18nKey="SettingsIntegrations.vtexSubtitle">
                    <ExternalLink
                        href={ExternalRoutes.VTEXInstructions[language] ?? ExternalRoutes.VTEXInstructions.GB}
                        variant="caption"
                        underline="hover"
                    >
                        {{ link: t('SettingsIntegrations.instructionsLink') }}
                    </ExternalLink>
                </Trans>
            }
            onPrimaryAction={onConnect}
            primaryActionText={t('buttons.connect')}
            primaryActionDisabled={isSubmitting || !isValid}
        >
            <Content>
                <Controller
                    control={control}
                    name="accountName"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            sx={{ gridArea: 'input1' }}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsIntegrations.accountName')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="applicationToken"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            sx={{ gridArea: 'input2' }}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsIntegrations.appToken')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="applicationKey"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            sx={{ gridArea: 'input3' }}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsIntegrations.appKey')}
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
            </Content>
        </Modal>
    );
};

export default VTEXModal;
